import type React from 'react';

import { type PrivacyPolicySignStatusDto } from '@/api/generated/models/privacy-policy-sign-status-dto';
import { type PageTitlesTranslation } from '@/assets/locales/pageTitles/pageTitles.translation.en.ts';
import { type RouteStateModel } from '@/model/RouteState.model.ts';

export enum RouteEnum {
  ADD_VACANCY = '/request-resource',

  ASSESSMENT_EDIT = '/assessments/:assessmentId/reports/:reportId',
  ASSESSMENT_ID = '/assessments/:assessmentId',
  ASSESSMENTS = '/assessments',
  CANDIDATES = '/candidates',
  CANDIDATES_ID = '/candidates/:candidateId',
  CERTIFICATES_MANAGEMENT = '/certificates-management',
  COMPANIES = '/companies',
  COMPANIES_ID = '/companies/:id',
  COMPANY_EMPLOYEE = '/company/:companyId/employee/:employeeId',
  DICTIONARIES = '/dictionaries',
  EDIT_VACANCY = '/edit-resource/:vacancyId',
  FORBIDDEN = '/forbidden',
  LOGIN = '/login',
  LOGIN_SUCCESSFUL = '/login-successful',
  MAINTENANCE = '/maintenance',
  MY_PROFILE = '/my-profile',
  NOT_AUTHORIZED = '/not-authorized',
  NOT_FOUND = '/not-found',
  OFFERINGS_MANAGEMENT = '/offerings-management',
  OVERVIEW = '/overview',
  PROFILE_SEARCH = '/search',
  PROFILE_SEARCH_NEW = '/search-new',
  PROJECT_DETAILS = '/projects/:id',
  RECRUITMENT_BOARD = '/request-tracker/',
  REPORTS = '/reports',
  ROOT = '/',
  SKILLS_MANAGEMENT = '/skills-management',

  USER_PROFILE = '/user-profile',
  USERS_INITIALIZE = '/users/initialize',
  USERS_SIGN_PRIVACY_POLICY = '/users/sign-privacy-policy',
  VACANCIES_CANDIDATE = '/vacancies/:vacancyId/candidates/:candidateId',
  VACANCY_ID = '/vacancies/:id',
  VACANCY_PROJECTS = '/projects',
  WAITING_FOR_APPROVAL = '/waiting-for-approval',
}

/**
 * Cache-backed display name for a detail route (OCD-311): the name is read
 * from the react-query cache the underlying view already populated — the
 * declaration never issues a fetch of its own. `select` composes the name
 * from the cached DTO and returns null while data is missing, keeping the
 * static label as fallback.
 */
export interface EntityBreadcrumbName {
  /** Route param carrying the entity id (e.g. `id`, `candidateId`). */
  param: string;
  queryKey(entityId: string): unknown[];
  select(entity: unknown): string | null;
}

/**
 * Runtime context a route-level `dynamicTitle` may read (OCD-305): kept
 * minimal on purpose so route declarations stay free of React hooks while
 * still resolving state-dependent titles (copy vs add, privacy-policy
 * variant).
 */
export interface RouteBreadcrumbContextModel {
  privacyPolicyStatus: PrivacyPolicySignStatusDto;
}

/**
 * Breadcrumb declaration for a route (OCD-305): a static i18n label plus an
 * optional parent route. `dynamicTitle` lets a view whose heading depends on
 * runtime state (add/edit/copy vacancy, privacy-policy variant) override the
 * static label without reintroducing imperative page-title state. `entityName`
 * (OCD-311) replaces the static label with the entity's display name once the
 * view's data is in the query cache.
 */
export interface RouteBreadcrumbModel {
  /** Overrides `parent` from navigation state, for views reachable from more than one list. */
  dynamicParent?: (locationState: unknown) => RouteEnum | undefined;
  dynamicTitle?: (
    route: RouteStateModel,
    context: RouteBreadcrumbContextModel,
  ) => keyof PageTitlesTranslation;
  entityName?: EntityBreadcrumbName;
  label: keyof PageTitlesTranslation;
  parent?: RouteEnum;
}

export interface RouteModel {
  breadcrumb?: RouteBreadcrumbModel;
  loadView: () => Promise<{ default: React.FC<{ route: RouteStateModel }> }>;
  path: RouteEnum;
}
